declare const $: any; // jQuery global
import { BookingFormValidator } from "./booking-form-validator";
import { resetButtonLoading, showButtonLoading } from "../ui/button-loading";
import type { CustomValidationService } from "../validation/validation-service";

// Used in instances of non-payment; exposed globally for inline onclick
function submitEventCheckout(): void {
    if (!BookingFormValidator.validateBookingForm("EventBookingForm")) return;

    const form = document.getElementById("EventBookingForm") as HTMLFormElement | null;
    if (!form) return;

    showButtonLoading(document.getElementById("updateButton") as HTMLButtonElement | null);
    form.submit();
}

(window as any).submitEventCheckout = submitEventCheckout;

/**
 * The event checkout (Views/Components/EventBookingCheckout). jQuery Validate checks the form
 * when the order is placed, as on Register and the tickets step; aspnet-client-validation is let
 * go of the form so the two don't both write its messages.
 */
export function initCheckoutPage(paymentService: any, validationService?: CustomValidationService): void {
    if (!$) return;

    const form = document.getElementById("EventBookingForm") as HTMLFormElement | null;
    if (!form) return;

    validationService?.release(form);

    const terms = form.querySelector<HTMLInputElement>("#TermsAllAgreed");

    // Use shared validator to setup terms validation
    if (terms) {
        BookingFormValidator.setupTermsValidation("EventBookingForm");
    }

    // Wire invoice button -> payment service method
    const invoiceBtn = document.getElementById("invoiceButton") as HTMLElement | null;
    if (invoiceBtn) {
        invoiceBtn.setAttribute("type", "button"); // hard guarantee
        invoiceBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            if (!paymentService || typeof paymentService.submitEventInvoiceFromButton !== "function") {
                console.error("[checkout] paymentService missing submitEventInvoiceFromButton");
                return;
            }

            paymentService.submitEventInvoiceFromButton(invoiceBtn);
        });
    }

    const poInput = document.getElementById("PoNumber") as HTMLInputElement | null;
    const poError = document.getElementById("PoNumberError") as HTMLElement | null;

    if (poInput) {
        poInput.addEventListener("input", () => {
            if (poInput.value.trim().length) {
                poInput.classList.remove("input-validation-error");
                if (poError) {
                    poError.textContent = "";
                    poError.classList.add("d-none");
                }
            }
        });
    }

    // Wire up event discount code button
    const discountBtn = document.getElementById("applyEventDiscountBtn") as HTMLButtonElement | null;
    if (discountBtn) {
        discountBtn.addEventListener("click", applyEventDiscount);
    }

    const discountInput = document.getElementById("eventDiscountInput") as HTMLInputElement | null;
    if (discountInput) {
        discountInput.addEventListener("keydown", (e) => {
            if (e.key === "Enter") {
                e.preventDefault();
                applyEventDiscount();
            }
        });
    }

    // Wire up remove event discount button
    const removeDiscountBtn = document.getElementById("removeEventDiscountBtn") as HTMLButtonElement | null;
    if (removeDiscountBtn) {
        removeDiscountBtn.addEventListener("click", removeEventDiscount);
    }
}

async function applyEventDiscount(): Promise<void> {
    const input = document.getElementById("eventDiscountInput") as HTMLInputElement | null;
    const msg = document.getElementById("eventDiscountMessage") as HTMLElement | null;
    const container = document.querySelector<HTMLElement>(".voucher-code-form[data-event-id]");

    if (!input || !container) return;

    const code = input.value.trim();
    if (!code) return;

    const eventId = container.dataset.eventId;
    if (!eventId) return;

    // "Applying..." until the page reloads with the code, or the code is turned down.
    const button = document.getElementById("applyEventDiscountBtn") as HTMLButtonElement | null;
    if (button?.disabled) return;
    showButtonLoading(button);

    if (msg) {
        msg.style.display = "none";
        msg.textContent = "";
    }

    try {
        const response = await fetch("/umbraco/surface/EventBookingSurface/ApplyEventDiscount", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ code, eventId }),
        });

        // Rate limited (ServiceCollectionExtensions.AddRateLimits), with no JSON body.
        if (response.status === 429) {
            if (msg) {
                msg.innerHTML = `<span class="text-danger">Too many attempts. Please wait a minute and try again.</span>`;
                msg.style.display = "block";
            }
            resetButtonLoading(button);
            return;
        }

        const result = await response.json();

        // On success the page reloads to reprice the booking, so say so rather than leave
        // "Discount applied!" showing against the old totals.
        if (msg) {
            const text = result.success ? "Discount applied, refreshing..." : result.message;
            msg.innerHTML = `<span class="${result.success ? "text-success" : "text-danger"}">${text}</span>`;
            msg.style.display = "block";
        }

        if (result.success) {
            window.location.reload();
        } else {
            resetButtonLoading(button);
        }
    } catch {
        if (msg) {
            msg.innerHTML = `<span class="text-danger">Something went wrong. Please try again.</span>`;
            msg.style.display = "block";
        }
        resetButtonLoading(button);
    }
}

async function removeEventDiscount(): Promise<void> {
    const container = document.querySelector<HTMLElement>(".voucher-code-form[data-event-id]");
    if (!container) return;

    const eventId = container.dataset.eventId;
    if (!eventId) return;

    // "Removing..." until the page reloads without the code.
    const button = document.getElementById("removeEventDiscountBtn") as HTMLButtonElement | null;
    if (button?.disabled) return;
    showButtonLoading(button);

    try {
        const response = await fetch("/umbraco/surface/EventBookingSurface/RemoveEventDiscount", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ eventId }),
        });

        const result = await response.json();

        if (result.success) {
            window.location.reload();
        } else {
            resetButtonLoading(button);
        }
    } catch {
        console.error("Failed to remove event discount");
        resetButtonLoading(button);
    }
}